import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout from "./components/AdminLayout";

const TOKEN_KEYS = ["token", "authToken", "accessToken", "jwt"];
function readToken() {
  try {
    for (const k of TOKEN_KEYS) {
      const t = localStorage.getItem(k);
      if (t && String(t).trim()) return String(t).trim();
    }
  } catch (_) {}
  return "";
}
function authHeaders() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}`, "x-auth-token": t } : {};
}

const EMPTY_FORM = { _id: null, name: "", description: "", price: "", durationDays: "", isActive: true, sortOrder: "" };

export default function AdminSubscriptionPlans() {
  const [plans,   setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState(null);
  const [deleting,setDeleting]= useState(null);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/subscription/admin/plans", { headers: authHeaders() });
      if (res.data?.success) setPlans(res.data.data || []);
    } catch (_) {
      showToast("error", "Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleEdit = (plan) => {
    setForm({
      _id: plan._id,
      name: plan.name,
      description: plan.description || "",
      price: String(plan.price),
      durationDays: String(plan.durationDays),
      isActive: plan.isActive !== false,
      sortOrder: String(plan.sortOrder || 0),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReset = () => setForm(EMPTY_FORM);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...(form._id ? { _id: form._id } : {}),
        name:         form.name.trim(),
        description:  form.description.trim(),
        price:        Number(form.price),
        durationDays: Number(form.durationDays),
        isActive:     form.isActive,
        sortOrder:    Number(form.sortOrder || 0),
      };
      const res = await axios.post("/api/subscription/admin/plan", payload, { headers: authHeaders() });
      if (res.data?.success) {
        showToast("success", form._id ? "Plan updated" : "Plan created");
        setForm(EMPTY_FORM);
        fetchPlans();
      } else {
        showToast("error", res.data?.message || "Save failed");
      }
    } catch (e) {
      showToast("error", e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this plan? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await axios.delete(`/api/subscription/admin/plan/${id}`, { headers: authHeaders() });
      showToast("success", "Plan deleted");
      fetchPlans();
    } catch (_) {
      showToast("error", "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  const styles = {
    page: { minHeight: "calc(100vh - 78px)", background: "#07459f", color: "#fff", padding: "32px 16px 80px" },
    wrap: { maxWidth: 860, margin: "0 auto" },
    title: { fontSize: "1.6rem", fontWeight: 800, marginBottom: 24 },
    card: { background: "#10284f", borderRadius: 16, padding: "24px", marginBottom: 28, border: "1px solid rgba(255,255,255,0.1)" },
    label: { display: "block", fontSize: "0.85rem", color: "rgba(200,220,255,0.7)", marginBottom: 5, marginTop: 14 },
    input: { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)", color: "#fff", fontSize: "0.95rem", boxSizing: "border-box" },
    row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
    btnPrimary: { padding: "11px 28px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#000", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" },
    btnSecondary: { padding: "11px 22px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.2)", background: "transparent", color: "rgba(200,220,255,0.8)", fontSize: "0.95rem", cursor: "pointer", marginLeft: 10 },
    btnDanger: { padding: "6px 14px", borderRadius: 8, border: "none", background: "rgba(239,68,68,0.15)", color: "#ef4444", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer" },
    btnEdit: { padding: "6px 14px", borderRadius: 8, border: "none", background: "rgba(255,212,59,0.12)", color: "#ffd43b", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", marginRight: 8 },
    planRow: { display: "flex", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: "1px solid rgba(255,255,255,0.07)" },
    badge: (active) => ({
      padding: "3px 10px", borderRadius: 12, fontSize: "0.75rem", fontWeight: 700,
      background: active ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.07)",
      color: active ? "#4ade80" : "rgba(200,220,255,0.5)",
    }),
    toast: (type) => ({
      position: "fixed", top: 88, right: 20, zIndex: 9999,
      padding: "13px 20px", borderRadius: 12, fontSize: "0.9rem", fontWeight: 600,
      background: type === "success" ? "#166534" : "#7f1d1d",
      border: `1px solid ${type === "success" ? "#22c55e" : "#ef4444"}`,
      color: "#fff", boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    }),
  };

  return (
    <AdminLayout>
      <div style={styles.page}>
        {toast && <div style={styles.toast(toast.type)}>{toast.type === "success" ? "✅" : "❌"} {toast.msg}</div>}

        <div style={styles.wrap}>
          <h1 style={styles.title}>👑 Subscription Plans</h1>

          {/* ── Form ── */}
          <div style={styles.card}>
            <h2 style={{ fontSize: "1.05rem", fontWeight: 700, marginBottom: 4 }}>
              {form._id ? "Edit Plan" : "Add New Plan"}
            </h2>
            <form onSubmit={handleSave}>
              <label style={styles.label}>Plan Name *</label>
              <input style={styles.input} value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. Monthly" />

              <label style={styles.label}>Description</label>
              <input style={styles.input} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description shown on plan card" />

              <div style={styles.row}>
                <div>
                  <label style={styles.label}>Price (₹) *</label>
                  <input style={styles.input} type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))} required placeholder="99" />
                </div>
                <div>
                  <label style={styles.label}>Duration (days) *</label>
                  <input style={styles.input} type="number" min="1" value={form.durationDays} onChange={(e) => setForm(f => ({ ...f, durationDays: e.target.value }))} required placeholder="30" />
                </div>
              </div>

              <div style={styles.row}>
                <div>
                  <label style={styles.label}>Sort Order</label>
                  <input style={styles.input} type="number" value={form.sortOrder} onChange={(e) => setForm(f => ({ ...f, sortOrder: e.target.value }))} placeholder="0" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                  <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.isActive} onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))} style={{ width: 16, height: 16 }} />
                    Active (visible to users)
                  </label>
                </div>
              </div>

              <div style={{ marginTop: 20 }}>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>
                  {saving ? "Saving…" : form._id ? "Update Plan" : "Create Plan"}
                </button>
                {form._id && (
                  <button type="button" style={styles.btnSecondary} onClick={handleReset}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* ── Plan List ── */}
          <div style={styles.card}>
            <h2 style={{ fontSize: "1.05rem", fontWeight: 700, marginBottom: 16 }}>All Plans</h2>
            {loading ? (
              <p style={{ color: "rgba(200,220,255,0.6)", fontSize: "0.95rem" }}>Loading…</p>
            ) : plans.length === 0 ? (
              <p style={{ color: "rgba(200,220,255,0.6)", fontSize: "0.95rem" }}>No plans yet. Create one above.</p>
            ) : (
              plans.map((p) => (
                <div key={p._id} style={styles.planRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: "0.97rem" }}>{p.name}</strong>
                    <span style={{ marginLeft: 10, fontSize: "0.82rem", color: "rgba(200,220,255,0.6)" }}>
                      ₹{p.price} · {p.durationDays} days
                    </span>
                    {p.description && (
                      <span style={{ marginLeft: 8, fontSize: "0.8rem", color: "rgba(200,220,255,0.45)" }}>— {p.description}</span>
                    )}
                  </div>
                  <span style={styles.badge(p.isActive)}>{p.isActive ? "Active" : "Inactive"}</span>
                  <button style={styles.btnEdit} onClick={() => handleEdit(p)}>Edit</button>
                  <button style={styles.btnDanger} onClick={() => handleDelete(p._id)} disabled={deleting === p._id}>
                    {deleting === p._id ? "…" : "Delete"}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
